import React, { Fragment, useState } from "react";
import { useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Modal } from "react-bootstrap";
import { toast } from "react-toastify";

import {
    getExecAdmins,
    createExecAdmin,
    updateExecAdmin,
    deactivateExecAdmin,
    resendExecAdminVerification,
} from "../../services/UserService";
import { formatKenyanPhoneNumber } from "../../utils/helpers";
import { scrollToPageTop } from "../../utils/scrollToPageTop";

const EMPTY_FORM = {
    first_name: '',
    last_name: '',
    preferred_name: '',
    email: '',
    phone: '',
};

const statusBadgeClass = (admin) => {
    if (admin.is_deleted)             return 'badge-danger';
    if (admin.status === 'active')    return 'badge-success';
    if (admin.status === 'suspended') return 'badge-warning';
    return 'badge-secondary';
};

const statusLabel = (admin) => admin.is_deleted ? 'Deleted' : admin.status_label;

const StatusBadge = ({ admin }) => (
    <span className={`badge badge-rounded ${statusBadgeClass(admin)}`}>
        {statusLabel(admin)}
    </span>
);

const PaginationBar = ({ page, lastPage, isLoading, onPrev, onNext }) => {
    if (lastPage <= 1) return null;
    return (
        <div className="d-flex justify-content-center align-items-center gap-3 px-3 py-2 border-top">
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page <= 1 || isLoading}
                onClick={onPrev}
                aria-label="Previous page"
            >
                <i className="fa fa-angle-left me-1" />
                Prev
            </button>
            <span className="text-muted small">Page {page} of {lastPage}</span>
            <button
                type="button"
                className="btn btn-sm btn-link p-0 text-decoration-none"
                disabled={page >= lastPage || isLoading}
                onClick={onNext}
                aria-label="Next page"
            >
                Next
                <i className="fa fa-angle-right ms-1" />
            </button>
        </div>
    );
};

const ManageExecAdmins = () => {
    const queryClient = useQueryClient();
    const { authUser } = useSelector(state => state.auth);

    const [showCreateModal, setShowCreateModal]       = useState(false);
    const [showEditModal, setShowEditModal]           = useState(false);
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [selectedAdmin, setSelectedAdmin]           = useState(null);
    const [currentPage, setCurrentPage]               = useState(1);
    const [createFormData, setCreateFormData]         = useState(EMPTY_FORM);
    const [editFormData, setEditFormData]             = useState(EMPTY_FORM);

    const { data, isPending, isError, isFetching } = useQuery({
        queryKey: ['exec-admins', currentPage],
        queryFn: () => getExecAdmins(currentPage),
        staleTime: 5 * 60 * 1000,
    });

    const execAdmins = data?.data?.data ?? [];
    const meta       = data?.data?.meta ?? {};
    const totalPages = meta.last_page ?? 1;

    const createMutation = useMutation({
        mutationFn: createExecAdmin,
        onSuccess: async () => {
            toast.success("Executive admin created. They'll receive an email to verify their address.");
            await queryClient.invalidateQueries({ queryKey: ['exec-admins'] });
            setShowCreateModal(false);
            setCreateFormData(EMPTY_FORM);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || "Failed to create executive admin.");
        },
    });

    const editMutation = useMutation({
        mutationFn: ({ userId, data }) => updateExecAdmin(userId, data),
        onSuccess: async (response) => {
            const verificationPinSent = response?.data?.verification_pin_sent === true;
            toast.success(
                verificationPinSent
                    ? "Executive admin updated. A fresh verification pin was sent to the new email."
                    : "Executive admin updated successfully."
            );
            await queryClient.invalidateQueries({ queryKey: ['exec-admins'] });
            setShowEditModal(false);
            setSelectedAdmin(null);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || "Failed to update executive admin.");
        },
    });

    const resendVerificationMutation = useMutation({
        mutationFn: (userId) => resendExecAdminVerification(userId),
        onSuccess: (response) => {
            toast.success(response?.data?.message || "Verification pin sent.");
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || "Could not send the verification pin. Try again in a moment.");
        },
    });

    const deactivateMutation = useMutation({
        mutationFn: (userId) => deactivateExecAdmin(userId),
        onSuccess: async () => {
            toast.success("Executive admin deactivated.");
            await queryClient.invalidateQueries({ queryKey: ['exec-admins'] });
            setShowDeactivateModal(false);
            setSelectedAdmin(null);
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message || "Failed to deactivate executive admin.");
        },
    });

    const handleCreateInputChange = (e) => {
        const value = e.target.name === 'phone' ? formatKenyanPhoneNumber(e.target.value) : e.target.value;
        setCreateFormData(prev => ({ ...prev, [e.target.name]: value }));
    };

    const handleEditInputChange = (e) => {
        const value = e.target.name === 'phone' ? formatKenyanPhoneNumber(e.target.value) : e.target.value;
        setEditFormData(prev => ({ ...prev, [e.target.name]: value }));
    };

    const handleCreate = (e) => {
        e.preventDefault();
        createMutation.mutate(createFormData);
    };

    const handleEditOpen = (admin) => {
        setSelectedAdmin(admin);
        setEditFormData({
            first_name:     admin.first_name,
            last_name:      admin.last_name,
            preferred_name: admin.preferred_name ?? '',
            email:          admin.email,
            phone:          admin.phone ?? '',
        });
        setShowEditModal(true);
    };

    const handleEdit = (e) => {
        e.preventDefault();
        editMutation.mutate({ userId: selectedAdmin.id, data: editFormData });
    };

    const handleDeactivateOpen = (admin) => {
        setSelectedAdmin(admin);
        setShowDeactivateModal(true);
    };

    const handleDeactivate = () => {
        deactivateMutation.mutate(selectedAdmin.id);
    };

    const handleCloseCreateModal = () => {
        setShowCreateModal(false);
        setCreateFormData(EMPTY_FORM);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setSelectedAdmin(null);
    };

    const handleCloseDeactivateModal = () => {
        setShowDeactivateModal(false);
        setSelectedAdmin(null);
    };

    const renderActionButtons = (admin, { align = 'start' } = {}) => {
        if (admin.is_deleted) return null;
        const canDeactivate = admin.id !== authUser.id && admin.status !== 'inactive';
        const isAwaitingVerification = admin.email_verified === false;
        const resendingForThisAdmin =
            resendVerificationMutation.isPending && resendVerificationMutation.variables === admin.id;
        return (
            <div className={`d-flex gap-2 flex-wrap ${align === 'end' ? 'justify-content-end' : ''}`}>
                <button
                    className="btn btn-sm btn-outline-info"
                    onClick={() => handleEditOpen(admin)}
                    aria-label={`Edit ${admin.full_name}`}
                >
                    <i className="fa fa-pencil me-1" aria-hidden="true" />
                    Edit
                </button>
                {isAwaitingVerification && (
                    <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => resendVerificationMutation.mutate(admin.id)}
                        disabled={resendingForThisAdmin}
                        aria-label={`Resend verification pin to ${admin.full_name}`}
                    >
                        {resendingForThisAdmin && <i className="fa fa-spinner fa-spin me-1" />}
                        <i className="fa fa-envelope me-1" aria-hidden="true" />
                        Resend verification
                    </button>
                )}
                {canDeactivate && (
                    <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => handleDeactivateOpen(admin)}
                        aria-label={`Deactivate ${admin.full_name}`}
                    >
                        <i className="fa fa-ban me-1" aria-hidden="true" />
                        Deactivate
                    </button>
                )}
            </div>
        );
    };

    const renderAwaitingVerificationBadge = (admin) => {
        if (admin.email_verified !== false) return null;
        return (
            <span
                className="badge badge-rounded badge-warning ms-2"
                title="This account has not verified their email yet."
            >
                Awaiting verification
            </span>
        );
    };

    const renderNameWithSelfBadge = (admin) => (
        <span className="d-inline-flex align-items-center flex-wrap gap-3">
            <span className="fw-semibold" style={{ overflowWrap: 'anywhere' }}>{admin.full_name}</span>
            {admin.id === authUser.id && (
                <span className="badge badge-rounded badge-info">You</span>
            )}
            {renderAwaitingVerificationBadge(admin)}
        </span>
    );

    return (
        <Fragment>
            <div className="row">
                <div className="col-12">
                    <div className="card">
                        <div className="card-header d-flex justify-content-end align-items-center">
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(true)}
                                className="btn btn-primary d-none d-lg-inline-flex"
                            >
                                Add Executive Admin
                            </button>
                            <button
                                type="button"
                                onClick={() => setShowCreateModal(true)}
                                className="btn btn-primary rounded-circle d-inline-flex d-lg-none align-items-center justify-content-center shadow-sm"
                                style={{ width: '2.5rem', height: '2.5rem', padding: 0 }}
                                title="Add executive admin"
                                aria-label="Add executive admin"
                            >
                                <i className="fas fa-plus" aria-hidden="true" />
                            </button>
                        </div>
                        <div className="card-body p-0">
                            {isPending ? (
                                <div className="d-flex justify-content-center py-4">
                                    <span className="spinner-border spinner-border-sm text-primary" role="status" />
                                </div>
                            ) : isError ? (
                                <p className="text-center text-danger py-4 mb-0">
                                    Failed to load executive admins. Please try again.
                                </p>
                            ) : execAdmins.length === 0 ? (
                                <p className="text-center text-muted py-4 mb-0">
                                    No executive admins.
                                </p>
                            ) : (
                                <>
                                    <div className="table-responsive d-none d-xl-block px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">Name</th>
                                                    <th scope="col">Email</th>
                                                    <th scope="col">Phone</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col">Date Added</th>
                                                    <th scope="col" className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {execAdmins.map((admin) => (
                                                    <tr key={admin.id}>
                                                        <td>{renderNameWithSelfBadge(admin)}</td>
                                                        <td style={{ overflowWrap: 'anywhere' }}>{admin.email}</td>
                                                        <td className="text-nowrap">{admin.phone || <span className="text-muted">Not set</span>}</td>
                                                        <td><StatusBadge admin={admin} /></td>
                                                        <td className="text-nowrap">{admin.created_when}</td>
                                                        <td className="text-end">{renderActionButtons(admin, { align: 'end' })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="table-responsive d-none d-md-block d-xl-none px-3 pt-2">
                                        <table className="table verticle-middle mb-0">
                                            <thead>
                                                <tr>
                                                    <th scope="col">User</th>
                                                    <th scope="col">Status</th>
                                                    <th scope="col" className="text-end">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {execAdmins.map((admin) => (
                                                    <tr key={admin.id}>
                                                        <td>
                                                            <div>{renderNameWithSelfBadge(admin)}</div>
                                                            <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>{admin.email}</div>
                                                            <div className="small text-muted">{admin.phone}</div>
                                                            <div className="small text-muted mt-1">
                                                                <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                                {admin.created_when}
                                                            </div>
                                                        </td>
                                                        <td><StatusBadge admin={admin} /></td>
                                                        <td className="text-end">{renderActionButtons(admin, { align: 'end' })}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <ul className="list-group list-group-flush d-block d-md-none">
                                        {execAdmins.map((admin) => (
                                            <li key={admin.id} className="list-group-item py-3">
                                                <div className="d-flex align-items-center gap-3 mb-1 flex-wrap">
                                                    {renderNameWithSelfBadge(admin)}
                                                    <StatusBadge admin={admin} />
                                                </div>
                                                <div className="small text-muted" style={{ overflowWrap: 'anywhere' }}>{admin.email}</div>
                                                <div className="small text-muted">{admin.phone}</div>
                                                <div className="small text-muted">
                                                    <i className="bi bi-calendar-event me-1" aria-hidden="true" />
                                                    {admin.created_when}
                                                </div>
                                                {!admin.is_deleted && (
                                                    <div className="mt-2">{renderActionButtons(admin)}</div>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </>
                            )}
                            <PaginationBar
                                page={currentPage}
                                lastPage={totalPages}
                                isLoading={isFetching}
                                onPrev={() => { setCurrentPage(p => Math.max(p - 1, 1)); scrollToPageTop(); }}
                                onNext={() => { setCurrentPage(p => Math.min(p + 1, totalPages)); scrollToPageTop(); }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showCreateModal} onHide={handleCloseCreateModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Create Executive Admin</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleCreate}>
                    <Modal.Body>
                        <div className="mb-3">
                            <label className="form-label">First Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="first_name"
                                className="form-control"
                                value={createFormData.first_name}
                                onChange={handleCreateInputChange}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Last Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="last_name"
                                className="form-control"
                                value={createFormData.last_name}
                                onChange={handleCreateInputChange}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Preferred Name</label>
                            <input
                                type="text"
                                name="preferred_name"
                                className="form-control"
                                value={createFormData.preferred_name}
                                onChange={handleCreateInputChange}
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                name="email"
                                className="form-control"
                                value={createFormData.email}
                                onChange={handleCreateInputChange}
                                required
                                maxLength={254}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Phone <span className="text-muted fw-normal">(optional)</span></label>
                            <input
                                type="text"
                                name="phone"
                                className="form-control"
                                value={createFormData.phone}
                                onChange={handleCreateInputChange}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseCreateModal}
                            disabled={createMutation.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={createMutation.isPending}
                        >
                            {createMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Create
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal show={showEditModal} onHide={handleCloseEditModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Edit Executive Admin</Modal.Title>
                </Modal.Header>
                <form onSubmit={handleEdit}>
                    <Modal.Body>
                        <div className="mb-3">
                            <label className="form-label">First Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="first_name"
                                className="form-control"
                                value={editFormData.first_name}
                                onChange={handleEditInputChange}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Last Name <span className="text-danger">*</span></label>
                            <input
                                type="text"
                                name="last_name"
                                className="form-control"
                                value={editFormData.last_name}
                                onChange={handleEditInputChange}
                                required
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Preferred Name</label>
                            <input
                                type="text"
                                name="preferred_name"
                                className="form-control"
                                value={editFormData.preferred_name}
                                onChange={handleEditInputChange}
                                maxLength={50}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Email <span className="text-danger">*</span></label>
                            <input
                                type="email"
                                name="email"
                                className="form-control"
                                value={editFormData.email}
                                onChange={handleEditInputChange}
                                required
                                maxLength={254}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Phone <span className="text-muted fw-normal">(optional)</span></label>
                            <input
                                type="text"
                                name="phone"
                                className="form-control"
                                value={editFormData.phone}
                                onChange={handleEditInputChange}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={handleCloseEditModal}
                            disabled={editMutation.isPending}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={editMutation.isPending}
                        >
                            {editMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                            Save Changes
                        </button>
                    </Modal.Footer>
                </form>
            </Modal>

            <Modal show={showDeactivateModal} onHide={handleCloseDeactivateModal} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Deactivate Executive Admin</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-0">
                        Are you sure you want to deactivate{' '}
                        <strong>{selectedAdmin?.full_name}</strong>? They will no longer be able to log in.
                    </p>
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={handleCloseDeactivateModal}
                        disabled={deactivateMutation.isPending}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-danger"
                        onClick={handleDeactivate}
                        disabled={deactivateMutation.isPending}
                    >
                        {deactivateMutation.isPending && <i className="fa fa-spinner fa-spin me-1" />}
                        Deactivate
                    </button>
                </Modal.Footer>
            </Modal>
        </Fragment>
    );
};

export default ManageExecAdmins;
